import { BROWSER_LOCAL_CACHE_SCHEMA_VERSION } from './config'
import type { BrowserLocalCacheNamespace } from './types'

const normalizeKeyPart = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[|]+/g, '/')

export const stableHashString = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

export const resolveEnvironmentScope = (apiBaseUrl?: string, environment?: string): string => {
  const explicitEnvironment = normalizeKeyPart(environment)
  const baseUrl = normalizeKeyPart(apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : 'unknown-api'))
  const env = explicitEnvironment || (typeof window !== 'undefined' ? window.location.hostname : 'unknown')
  return `${env}:${stableHashString(baseUrl)}`
}

export const resolveUserScope = (input: { userId?: string | number | null; authScope?: string | null }): string => {
  const userId = normalizeKeyPart(input.userId)
  if (userId) {
    return `user:${stableHashString(userId)}`
  }
  const authScope = normalizeKeyPart(input.authScope)
  if (authScope) {
    return `auth:${stableHashString(authScope)}`
  }
  return 'anonymous'
}

export const createBrowserLocalCacheNamespace = (input: {
  apiBaseUrl?: string
  environment?: string
  userId?: string | number | null
  authScope?: string | null
}): BrowserLocalCacheNamespace => {
  const environmentScope = resolveEnvironmentScope(input.apiBaseUrl, input.environment)
  return {
    appId: 'yijianmai',
    environment: environmentScope.split(':')[0] || 'unknown',
    apiBaseHash: environmentScope.split(':')[1] || stableHashString('unknown-api'),
    userScope: resolveUserScope(input),
    schemaVersion: BROWSER_LOCAL_CACHE_SCHEMA_VERSION,
  }
}

export const buildLocalCacheKey = (
  namespace: BrowserLocalCacheNamespace,
  resourceName: string,
  parts: Array<string | number | boolean | null | undefined>
): string => {
  const cleanParts = parts.map(normalizeKeyPart).filter(Boolean)
  return [
    namespace.appId,
    `schema:${namespace.schemaVersion}`,
    `env:${namespace.environment}:${namespace.apiBaseHash}`,
    namespace.userScope,
    normalizeKeyPart(resourceName),
    ...cleanParts,
  ].join('|')
}

export const buildImageCacheKey = (
  namespace: BrowserLocalCacheNamespace,
  canonicalUrl: string,
  version?: string | number | null
): string => buildLocalCacheKey(namespace, 'image', [stableHashString(canonicalUrl), version || 'unversioned'])

export const buildApiCacheKey = (
  namespace: BrowserLocalCacheNamespace,
  method: string,
  url: string,
  varyParts: Array<string | number | boolean | null | undefined> = []
): string => buildLocalCacheKey(namespace, 'api', [method.toUpperCase(), stableHashString(url), ...varyParts])

